import { FC, useState, useEffect, useCallback, FormEvent } from 'react';
import { Plus, Edit, Trash2, Eye, Check, X } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { DataTable, Column } from '../../components/admin/DataTable';
import { SearchInput } from '../../components/admin/SearchInput';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { FormModal } from '../../components/admin/FormModal';
import { adminApi } from '../../api/adminApi';
import type {
  AdminTherapist,
  TherapistListQuery,
  Pagination,
  CreateTherapistRequest,
  UpdateTherapistRequest,
} from '../../types/admin.types';

type PageState = 'loading' | 'loaded' | 'error';
type ModalState = 'closed' | 'create' | 'edit' | 'view' | 'delete';

const initialFormData: CreateTherapistRequest = {
  email: '',
  password: '',
  name: '',
  phone: '',
  bio: '',
  qualifications: [],
  specializations: [],
  yearsOfExperience: 0,
};

export const TherapistsPage: FC = () => {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [therapists, setTherapists] = useState<AdminTherapist[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [query, setQuery] = useState<TherapistListQuery>({ page: 1, limit: 10 });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal state
  const [modalState, setModalState] = useState<ModalState>('closed');
  const [selectedTherapist, setSelectedTherapist] = useState<AdminTherapist | null>(null);
  const [formData, setFormData] = useState<CreateTherapistRequest>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Temporary state for array fields
  const [qualificationsInput, setQualificationsInput] = useState('');
  const [specializationsInput, setSpecializationsInput] = useState('');

  const fetchTherapists = useCallback(async () => {
    try {
      setPageState('loading');
      const response = await adminApi.listTherapists(query);
      setTherapists(response.data);
      setPagination(response.pagination);
      setPageState('loaded');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load therapists');
      setPageState('error');
    }
  }, [query]);

  useEffect(() => {
    fetchTherapists();
  }, [fetchTherapists]);

  const handleSearch = (search: string) => {
    setQuery((prev) => ({ ...prev, search, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setQuery((prev) => ({ ...prev, page }));
  };

  const handleFilterChange = (key: keyof TherapistListQuery, value: string) => {
    setQuery((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
  };

  const openCreateModal = () => {
    setFormData(initialFormData);
    setQualificationsInput('');
    setSpecializationsInput('');
    setFormErrors({});
    setModalState('create');
  };

  const openEditModal = (therapist: AdminTherapist) => {
    setSelectedTherapist(therapist);
    setFormData({
      email: therapist.email,
      password: '', // Not shown for edit
      name: therapist.name,
      phone: therapist.phone || '',
      bio: therapist.bio || '',
      qualifications: therapist.qualifications,
      specializations: therapist.specializations,
      yearsOfExperience: therapist.yearsOfExperience,
    });
    setQualificationsInput(therapist.qualifications.join(', '));
    setSpecializationsInput(therapist.specializations.join(', '));
    setFormErrors({});
    setModalState('edit');
  };

  const openViewModal = (therapist: AdminTherapist) => {
    setSelectedTherapist(therapist);
    setModalState('view');
  };

  const openDeleteModal = (therapist: AdminTherapist) => {
    setSelectedTherapist(therapist);
    setModalState('delete');
  };

  const closeModal = () => {
    setModalState('closed');
    setSelectedTherapist(null);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (modalState === 'create') {
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Invalid email format';
      }

      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
    }

    if (!formData.bio.trim()) {
      errors.bio = 'Bio is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Parse comma-separated arrays
      const qualifications = qualificationsInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const specializations = specializationsInput
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      if (modalState === 'create') {
        await adminApi.createTherapist({
          ...formData,
          qualifications,
          specializations,
        });
        setSuccessMessage('Therapist created successfully');
      } else if (modalState === 'edit' && selectedTherapist) {
        const updateData: UpdateTherapistRequest = {
          name: formData.name,
          phone: formData.phone || undefined,
          bio: formData.bio,
          qualifications,
          specializations,
          yearsOfExperience: formData.yearsOfExperience,
        };
        await adminApi.updateTherapist(selectedTherapist.id, updateData);
        setSuccessMessage('Therapist updated successfully');
      }

      closeModal();
      fetchTherapists();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTherapist) return;

    setIsSubmitting(true);
    try {
      await adminApi.deleteTherapist(selectedTherapist.id);
      setSuccessMessage('Therapist deactivated successfully');
      closeModal();
      fetchTherapists();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to deactivate therapist');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (
    therapist: AdminTherapist,
    field: 'isActive' | 'isAvailable'
  ) => {
    try {
      await adminApi.updateTherapist(therapist.id, {
        [field]: !therapist[field],
      });
      setSuccessMessage(`Therapist ${field === 'isActive' ? 'status' : 'availability'} updated`);
      fetchTherapists();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update therapist');
    }
  };

  const columns: Column<AdminTherapist>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (therapist) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-slate-100">{therapist.name}</p>
          <p className="text-sm text-gray-500 dark:text-slate-400">{therapist.email}</p>
        </div>
      ),
    },
    {
      key: 'specializations',
      header: 'Specializations',
      render: (therapist) => (
        <div className="max-w-xs">
          {therapist.specializations.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {therapist.specializations.slice(0, 2).map((spec) => (
                <span
                  key={spec}
                  className="inline-block rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800"
                >
                  {spec}
                </span>
              ))}
              {therapist.specializations.length > 2 && (
                <span className="text-xs text-gray-500">
                  +{therapist.specializations.length - 2} more
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">None</span>
          )}
        </div>
      ),
    },
    {
      key: 'yearsOfExperience',
      header: 'Experience',
      render: (therapist) => `${therapist.yearsOfExperience} years`,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (therapist) => <StatusBadge status={therapist.isActive ? 'active' : 'inactive'} />,
    },
    {
      key: 'isAvailable',
      header: 'Available',
      render: (therapist) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleStatus(therapist, 'isAvailable');
          }}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            therapist.isAvailable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {therapist.isAvailable ? (
            <>
              <Check className="h-3 w-3" /> Yes
            </>
          ) : (
            <>
              <X className="h-3 w-3" /> No
            </>
          )}
        </button>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Therapists</h2>
            <p className="mt-1 text-gray-500 dark:text-slate-400">Manage your therapist team</p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Therapist
          </button>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="text-green-800">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage('')}
              className="mt-1 text-sm text-green-600 hover:text-green-800"
            >
              Dismiss
            </button>
          </div>
        )}
        {errorMessage && pageState === 'loaded' && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-red-800">{errorMessage}</p>
            <button
              onClick={() => setErrorMessage('')}
              className="mt-1 text-sm text-red-600 hover:text-red-800"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <SearchInput
            value={query.search || ''}
            onChange={handleSearch}
            placeholder="Search therapists..."
            className="w-64"
          />
          <select
            value={query.isActive || ''}
            onChange={(e) => handleFilterChange('isActive', e.target.value)}
            className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 pl-3 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <select
            value={query.isAvailable || ''}
            onChange={(e) => handleFilterChange('isAvailable', e.target.value)}
            className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 py-2 pl-3 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Availability</option>
            <option value="true">Available</option>
            <option value="false">Unavailable</option>
          </select>
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={therapists}
          keyExtractor={(t) => t.id}
          isLoading={pageState === 'loading'}
          emptyMessage="No therapists found"
          pagination={pagination || undefined}
          onPageChange={handlePageChange}
          onRowClick={openViewModal}
          actions={(therapist) => (
            <>
              <button
                onClick={() => openViewModal(therapist)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                title="View details"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                onClick={() => openEditModal(therapist)}
                className="rounded p-1 text-blue-500 hover:bg-blue-50 hover:text-blue-700"
                title="Edit"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => openDeleteModal(therapist)}
                className="rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                title="Deactivate"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        />

        {/* Create/Edit Modal */}
        {(modalState === 'create' || modalState === 'edit') && (
          <FormModal
            isOpen
            onClose={closeModal}
            onSubmit={handleSubmit}
            title={modalState === 'create' ? 'Add New Therapist' : 'Edit Therapist'}
            submitText={modalState === 'create' ? 'Create' : 'Save Changes'}
            isLoading={isSubmitting}
            size="lg"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
              </div>

              {/* Email (only for create) */}
              {modalState === 'create' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>
              )}

              {/* Password (only for create) */}
              {modalState === 'create' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {formErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                  )}
                </div>
              )}

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Years of Experience */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Years of Experience
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.yearsOfExperience}
                  onChange={(e) =>
                    setFormData({ ...formData, yearsOfExperience: parseInt(e.target.value) || 0 })
                  }
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Qualifications */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Qualifications (comma-separated)
                </label>
                <input
                  type="text"
                  value={qualificationsInput}
                  onChange={(e) => setQualificationsInput(e.target.value)}
                  placeholder="e.g., Licensed Massage Therapist, Certified Sports Massage"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Specializations */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Specializations (comma-separated)
                </label>
                <input
                  type="text"
                  value={specializationsInput}
                  onChange={(e) => setSpecializationsInput(e.target.value)}
                  placeholder="e.g., Deep Tissue, Swedish, Sports Massage"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Bio */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Bio *</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={4}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {formErrors.bio && <p className="mt-1 text-sm text-red-600">{formErrors.bio}</p>}
              </div>
            </div>
          </FormModal>
        )}

        {/* View Modal */}
        {modalState === 'view' && selectedTherapist && (
          <FormModal
            isOpen
            onClose={closeModal}
            onSubmit={closeModal}
            title="Therapist Details"
            submitText="Close"
            cancelText=""
            size="lg"
          >
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm font-medium text-gray-500">Name</p>
                  <p className="text-gray-900">{selectedTherapist.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="text-gray-900">{selectedTherapist.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Phone</p>
                  <p className="text-gray-900">{selectedTherapist.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Experience</p>
                  <p className="text-gray-900">{selectedTherapist.yearsOfExperience} years</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <StatusBadge status={selectedTherapist.isActive ? 'active' : 'inactive'} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Availability</p>
                  <StatusBadge
                    status={selectedTherapist.isAvailable ? 'Available' : 'Unavailable'}
                    variant={selectedTherapist.isAvailable ? 'success' : 'default'}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Qualifications</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedTherapist.qualifications.length > 0 ? (
                    selectedTherapist.qualifications.map((qual) => (
                      <span
                        key={qual}
                        className="inline-block rounded bg-gray-100 px-2 py-1 text-sm text-gray-800"
                      >
                        {qual}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400">None listed</span>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Specializations</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedTherapist.specializations.length > 0 ? (
                    selectedTherapist.specializations.map((spec) => (
                      <span
                        key={spec}
                        className="inline-block rounded bg-blue-100 px-2 py-1 text-sm text-blue-800"
                      >
                        {spec}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400">None listed</span>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Bio</p>
                <p className="mt-1 text-gray-900">{selectedTherapist.bio || 'No bio provided'}</p>
              </div>
            </div>
          </FormModal>
        )}

        {/* Delete Confirmation Modal */}
        {modalState === 'delete' && selectedTherapist && (
          <ConfirmModal
            isOpen
            onClose={closeModal}
            onConfirm={handleDelete}
            title="Deactivate Therapist"
            message={`Are you sure you want to deactivate ${selectedTherapist.name}? They will no longer appear in the booking system.`}
            confirmText="Deactivate"
            variant="danger"
            isLoading={isSubmitting}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default TherapistsPage;
